import { defineComponent, reactive, ref, readonly, SetupContext } from '@vue/composition-api';
import HttpStatus from 'http-status-codes';
import vueRouter from '@/router/index';                                                             // ルート情報取得
import { OrganizationsList, TableHeader, OnColumns, ConfigIconCoordinate } from '@/shared/type';    // 組織一覧情報の型をimport
import SystemConst from '@/shared/system-const';                                                    // 定数定義をimport
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';          // 共通メッセージダイアログコンポーネント
import { DataOptions } from 'vuetify';                                                              // v-data-tableのupdateイベントの引数を利用するための型
import CommonTableFilter from '@/components/common/common-table-filter.component.vue';              // フィルタ用共通コンポーネント
import { TableFilterDefs, TableFilters } from '@/components/common/common-table-filter.component';
import CommonFunction from '@/shared/common-function';
import CommonOnColumnsMenu from '../common/common-on-columns-menu.component.vue';

// リアクティブな変数の型定義
export type reactiveData = {
    visibleOrganizationTable: boolean;                                   // 組織一覧表示・非表示
    visibleFilterMenu: boolean;                                          // フィルタメニュー表示・非表示
    windowSize: { x: number; y: number };                                // Windowサイズ
    organizationListItem: OrganizationsList[];                           // 組織一覧情報
    organizationDisplayTableHeaders: TableHeader[];                      // 組織一覧ヘッダ情報
    total: number;                                                       // 組織データ総数
    isLoading: boolean;                                                  // テーブルがロード中: true ロード中でない: false
    filterDefs: TableFilterDefs,                                         // フィルタ定義
    filters: TableFilters,                                               // フィルタ情報
    pageOptions: DataOptions,                                            // ページ情報・ソート情報
    outputExcelDialog: boolean;                                          // 一覧データダウンロードダイアログ
    isDownLoading: boolean;                                              // ファイルがダウンロード中: true ダウンロード中でない: false
    displayConfirmationDialog: boolean;                                  // Excel出力確認ダイアログ表示フラグ
    isFromAdd: boolean;                                                  // 登録・編集・削除画面からの遷移フラグ
};

export default defineComponent({
    components: { CommonMessageDialog, CommonTableFilter, CommonOnColumnsMenu },
    setup(props, ctx: SetupContext) {
        const tableHeight = SystemConst.TABLE_HEIGHT.ORGANIZATION_LIST;  // テーブル高さ調整用変数
        const isLoading = <boolean>false;                                // 一覧取得中状態
        const router = vueRouter;                                        // ルータ
        const commonRef = ref<any>(null);                                // 共通ダイアログ
        const columnRef = ref<any>(null);                                // 列設定    
        const tableHeaderClass: string[] = ['secondary subtitle-2'];     // 組織一覧のヘッダ色
        const page = ref<number>(1);                                     // ページ情報（状態管理用）
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;            // リスト表示件数定義
        const itemsPerPage = ref<number>(linesPerPage[1]);               // 表示件数(状態管理用)
        const sortBy = ref<string[]>([]);                                // ソートカラム（状態管理用）
        const sortDesc = ref<boolean[]>([]);                             // ソート昇順、降順（状態管理用）
        const child = ref<any>();                                        // テーブルフィルタ一覧
        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            visibleOrganizationTable: true,                              // 組織一覧表示・非表示
            visibleFilterMenu: false,                                    // フィルタメニュー表示・非表示
            windowSize: { x: 0, y: 0 },                                  // ウィンドウサイズ
            organizationListItem: [],                                    // 組織一覧情報
            organizationDisplayTableHeaders: [],                         // 組織一覧ヘッダー
            total: 0,                                                    // 組織データ総数
            isLoading: false,                                            // テーブルがロード中: true ロード中でない: false
            displayConfirmationDialog: true,                             // Excel出力確認ダイアログ表示フラグ
            outputExcelDialog: false,                                    // 一覧データダウンロードダイアログ
            isDownLoading: false,                                        // ファイルがダウンロード中: true ダウンロード中でない: false
            filterDefs: {                                                // フィルタ定義
                'organizationId': { title: '組織ID', types: 'KeyWordPartialMatch' },
                'affiliationCode': { title: '所属コード', types: 'KeyWordPartialMatch' },
                'displayOrder': { title: '表示順', types: 'NumRange' },
                'organizationName': { title: '組織名称', types: 'KeyWordPartialMatch' },
                'organizationNameKana': { title: '組織名称（かな）', types: 'KeyWordPartialMatch' },
                'locationName': { title: '地域', types: 'PullDown' },
                'organizationTypeName': { title: '組織型', types: 'PullDown' },
                'domain': { title: '組織ドメイン', types: 'KeyWordPartialMatch' },
                'representMail': { title: '代表メールアドレス', types: 'KeyWordPartialMatch' },
                'group': { title: 'グループ作成', types: 'CheckBox' },
                'canCms': { title: 'CMS利用可否', types: 'CheckBox' },
                'createdAt': { title: '登録日時', types: 'DatePeriod' },
                'updatedAt': { title: '更新日時', types: 'DatePeriod' }
            },
            pageOptions: {                                               // ページ情報・ソート情報
                page: 1, itemsPerPage: linesPerPage[1], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            filters: {},                                                 // フィルタ情報
            isFromAdd: false                                             // 登録・編集・削除画面からの遷移フラグ
        });
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;         // テーブルのデータ取得中に表示するテキスト
        const organizationInitialDisplayTableHeaders = [
            // 組織一覧ヘッダ情報(初期表示ヘッダー)
            // 組織ID
            {
                text: '組織ID',
                value: 'organizationId',
                width: '120px',
                class: tableHeaderClass
            },
            // 組織名称
            {
                text: '組織名称',
                value: 'organizationName',
                width: '190px',
                class: tableHeaderClass
            },
            // 地域
            {
                text: '地域',
                value: 'locationName',
                width: '190px',
                class: tableHeaderClass
            },
            // 組織型
            {
                text: '組織型',
                value: 'organizationTypeName',
                width: '190px',
                class: tableHeaderClass
            },
            // 組織ドメイン
            {
                text: '組織ドメイン',
                value: 'domain',
                width: '200px',
                class: tableHeaderClass
            },
            // CMS利用可否
            {
                text: 'CMS利用可否',
                value: 'canCms',
                width: '160px',
                class: tableHeaderClass
            },
            // 登録日時
            {
                text: '登録日時',
                value: 'createdAt',
                width: '200px',
                class: tableHeaderClass
            },
            // 列の管理メニュー
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];
        const organizationTableHeaders = [
            // 組織一覧ヘッダ情報
            // 組織ID
            {
                text: '組織ID',
                value: 'organizationId',
                width: '120px',
                class: tableHeaderClass
            },
            // 所属コード
            {
                text: '所属コード',
                value: 'affiliationCode',
                width: '190px',
                class: tableHeaderClass
            },
            // 表示順
            {
                text: '表示順',
                value: 'displayOrder',
                width: '120px',
                class: tableHeaderClass
            },
            // 組織名称
            {
                text: '組織名称',
                value: 'organizationName',
                width: '190px',
                class: tableHeaderClass
            },
            // 組織名称（かな）
            {
                text: '組織名称（かな）',
                value: 'organizationNameKana',
                width: '190px',
                class: tableHeaderClass
            },
            // 地域
            {
                text: '地域',
                value: 'locationName',
                width: '190px',
                class: tableHeaderClass
            },
            // 組織型
            {
                text: '組織型',
                value: 'organizationTypeName',
                width: '190px',
                class: tableHeaderClass
            },
            // 組織ドメイン
            {
                text: '組織ドメイン',
                value: 'domain',
                width: '200px',
                class: tableHeaderClass
            },
            // 代表メールアドレス
            {
                text: '代表メールアドレス',
                value: 'representMail',
                width: '200px',
                class: tableHeaderClass
            },
            // グループ作成
            {
                text: 'グループ作成',
                value: 'group',
                width: '200px',
                class: tableHeaderClass
            },
            // CMS利用可否
            {
                text: 'CMS利用可否',
                value: 'canCms',
                width: '160px',
                class: tableHeaderClass
            },
            // 登録日時
            {
                text: '登録日時',
                value: 'createdAt',
                width: '200px',
                class: tableHeaderClass
            },
            // 更新日時
            {
                text: '更新日時',
                value: 'updatedAt',
                width: '200px',
                class: tableHeaderClass
            },
            // 列の管理メニュー
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];
        const onColumns: OnColumns[] = [
            {
                text: '全て表示',
                value: false
            },
            {
                text: '組織ID',
                value: true
            },
            {
                text: '所属コード',
                value: false
            },
            {
                text: '表示順',
                value: false
            },
            {
                text: '組織名称',
                value: true
            },
            {
                text: '組織名称（かな）',
                value: false
            },
            {
                text: '地域',
                value: true
            },
            {
                text: '組織型',
                value: true
            },
            {
                text: '組織ドメイン',
                value: true
            },
            {
                text: '代表メールアドレス',
                value: false
            },
            {
                text: 'グループ作成',
                value: false
            },
            {
                text: 'CMS利用可否',
                value: true
            },
            {
                text: '登録日時',
                value: true
            },
            {
                text: '更新日時',
                value: false
            }
        ]

        /**
         * フィルタ条件のバリデーション
         */
        // 組織ID
        const organizationIdRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '組織ID')
        ];

        // 所属コード
        const affiliationCodeRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '所属コード')
        ];

        // 表示順
        const displayOrderRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['numRangeLength'], '表示順')
        ];

        // 組織名称
        const organizationNameRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '組織名称')
        ];

        // 組織名称（かな）
        const organizationNameKanaRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '組織名称（かな）')
        ];

        // 組織ドメイン
        const domainRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '組織ドメイン'),
        ];

        // 代表メールアドレス
        const representMailRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['textLength'], '代表メールアドレス'),
        ];

        // フィルタ用最大桁数
        const maxLength = {
            textLength: SystemConst.FILTER_MAX_LENGTH.FILTER_LENGTH,
            numRangeLength: SystemConst.FILTER_MAX_LENGTH.NUM_RANGE_LENGTH
        }

        /**
         * インスタンス生成された際に実行
         */
        created();

        /**
         * 組織一覧画面表示処理
         */
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-format-list-bulleted');

            // タイトル変更
            ctx.root.$store.commit('setMainTitle', '組織一覧');

            // 組織一覧情報をクリア
            state.organizationListItem = [];

            // 初期表示ヘッダ設定
            state.organizationDisplayTableHeaders = organizationInitialDisplayTableHeaders;

            // storeからフィルター情報を復元する
            state.filters = ctx.root.$store.state.organization.filters;

            // storeからページ情報を復元する
            page.value = ctx.root.$store.state.organization.page;
            itemsPerPage.value = ctx.root.$store.state.organization.itemsPerPage;

            // storeからソート情報を復元する
            sortBy.value = ctx.root.$store.state.organization.sortBy;
            sortDesc.value = ctx.root.$store.state.organization.sortDesc;

            // storeから登録等画面からの遷移フラグを復元する
            state.isFromAdd = ctx.root.$store.state.user.isFromAdd;

            // フィルタ初期設定（initFilter()を呼び出す)
            // 初期設定が完了するまでawaitで待つ
            await initFilter();

            // vuexにフィルタ情報が保持されているデータを復元する
            const filterIdList = [];
            const filterParameters = [];
            for (const key in state.filters) {
                if (Object.prototype.hasOwnProperty.call(state.filters, key)) {
                    const val = state.filters[key];
                    if (val.isEnabled) {
                        // 有効なフィルタのIDを保存する
                        filterIdList.push(key);
                        // 有効なフィルタ条件を保存する
                        filterParameters.push(val.condition);
                    }
                }
            }

            // 復元されたフィルタ情報がある場合
            if (filterIdList.length !== 0) {
                // 子コンポーネントのsetAllFiltersを呼び出し、フィルターを表示する(フィルタ更新処理から一覧が取得できる)
                child.value.setAllFilters(filterIdList, filterParameters);
            } else {
                // 組織一覧情報取得
                getOrganizations();
            }

            // 登録等画面からの遷移フラグにfalseを設定する
            state.isFromAdd = false;
        }

        /**
         * フィルタ初期設定
         * 
         * フィルタにチェックボックスやプルダウン等、DBから取得する必要のある条件がある場合、
         * 初期表示(created())で取得する。
         */
        async function initFilter() {
            // フィルタ条件の内容を取得
            // getFilterConditionsを呼び出す
            const response = await ctx.root.$store.dispatch('organization/getFilterConditions');

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {

                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // フィルタ（プルダウン・チェックボックス）に表示するデータを設定する
            // 地域
            state.filterDefs['locationName'].items = response.data.locations;
            // 組織型
            state.filterDefs['organizationTypeName'].items = response.data.organizationTypes;
            // グループ作成
            state.filterDefs['group'].chkItems = response.data.groups;
            // CMS利用可否
            state.filterDefs['canCms'].chkItems = response.data.canCms;

            // バリデーションを設定する
            // 組織ID
            state.filterDefs['organizationId'].validator = organizationIdRules;
            // 所属コード
            state.filterDefs['affiliationCode'].validator = affiliationCodeRules;
            // 表示順
            state.filterDefs['displayOrder'].startValidator = displayOrderRules;
            state.filterDefs['displayOrder'].endValidator = displayOrderRules;
            // 組織名称
            state.filterDefs['organizationName'].validator = organizationNameRules;
            // 組織名称（かな）
            state.filterDefs['organizationNameKana'].validator = organizationNameKanaRules;
            // 組織ドメイン
            state.filterDefs['domain'].validator = domainRules;
            // 代表メールアドレス
            state.filterDefs['representMail'].validator = representMailRules;
        }

        /**
         * フィルタ設定によるイベント
         * 
         * フィルタの条件を追加・削除したタイミングで呼ばれる。
         * このイベントでv-data-tableの表示更新を行うこと。
         * 
         * @param filters フィルタ条件
         */
        function updateFilter(filters: TableFilters) {
            // フィルタ条件をstate内へ保存する
            state.filters = filters;

            // 登録等画面からの遷移以外の場合
            if (!state.isFromAdd) {
                // 1ページへ戻す
                state.pageOptions.page = 1;
                page.value = 1;
            }

            // 一覧を表示更新する
            getOrganizations();
        }

        /**
         * 一覧のページ／ソート操作によるイベント
         * 
         * ページ操作とソート操作のタイミングで呼ばれる。
         * このイベントでv-data-tableの表示更新を行うこと。
         * 
         * @param options ページ／ソート情報
         */
        function updateOptions(options: DataOptions) {
            // ページ/ソート情報をstate内へ保存する
            state.pageOptions = options;

            // optionsの中身を比較して、いずれも変更されていなかった場合、
            // ページの更新を行わない
            if (page.value === options.page &&
                itemsPerPage.value === options.itemsPerPage &&
                sortBy.value === options.sortBy &&
                sortDesc.value === options.sortDesc
            ) {
                return;
            }

            // ページ情報を設定する
            page.value = options.page;
            itemsPerPage.value = options.itemsPerPage;

            // ソート情報を設定する
            sortBy.value = options.sortBy;
            sortDesc.value = options.sortDesc;

            // 一覧を表示更新する
            getOrganizations();
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        /**
         * 組織登録画面へ遷移する
         */
        async function navigationAdd() {
            // 組織登録画面へ遷移する
            router.push('/organizations/add');
        }

        /**
         * 組織編集画面へ遷移する
         * @param organizationId 組織ID
         */
        function navigationUpdate(organizationId: number) {
            // 組織編集画面へ遷移する
            router.push('/organizations/edit/' + organizationId);
        }

        /**
         * 組織削除画面へ遷移する
         * @param organizationId 組織ID
         */
        function navigationDelete(organizationId: number) {
            // 組織削除画面へ遷移する
            router.push('/organizations/delete/' + organizationId);
        }

        /**
         * 組織一覧取得処理呼び出し
         */
        async function getOrganizations() {

            // 組織一覧情報をクリア
            state.organizationListItem = [];

            // ローディング
            state.isLoading = true;

            // ストアにパラメータを格納
            ctx.root.$store.commit('organization/setPage', page.value);
            ctx.root.$store.commit('organization/setItemsPerPage', itemsPerPage.value);
            ctx.root.$store.commit('organization/setFilters', state.filters);
            ctx.root.$store.commit('organization/setSortBy', sortBy.value);
            ctx.root.$store.commit('organization/setSortDesc', sortDesc.value);

            // ページ数と１ページあたりの行数、及びフィルタ条件からAPIのパラメータを作成
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // 一覧表示用組織情報取得APIを呼び出す
            const response = await ctx.root.$store.dispatch('organization/getOrganizations', params);

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // ローディング解除
                state.isLoading = false;

                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // グループ作成に表示する文字列を設定する
            response.data.organizations.forEach(
                (x: OrganizationsList) => {
                    x.group = [
                        x.salaryGroup || '',
                        x.publicRelationsGroup || '',
                        x.officialDocumentGroup || ''
                    ].filter(y => y).join('、')
                }
            )

            // 組織一覧リストを表示する
            state.organizationListItem = response.data.organizations;

            // 組織データ総数をページフッターに設定する
            state.total = response.data.total;

            // ローディング解除
            state.isLoading = false;
        }

        /**
         * Excelファイルのダウンロード
         */
        async function outputExcelFile() {
            // 確認ダイアログを非表示にする
            state.displayConfirmationDialog = false;

            // ファイルダウンロード中にローダーを表示する
            state.isDownLoading = true;

            // ページ情報、ソート情報、及びフィルタ条件からAPIのパラメータを作成
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // Excelファイル名格納
            const fileName = SystemConst.EXCEL_API_PARAM.ORGANIZATIONS_FILE;

            // 組織storeのoutputExcelを呼び出す
            // パラメータ：作成したAPIのパラメータ（ソート情報、及びフィルタ条件のみ）
            //            Excelファイル名
            const response = await ctx.root.$store.dispatch('organization/outputExcel',
                {
                    params: {
                        sortKey: params.sortKey,
                        sortOrder: params.sortOrder,
                        filterParams: params.filterParams
                    },
                    fileName
                });


            // ファイルダウンロード中表示するローダーを非表示にする
            state.isDownLoading = false;

            // Excel出力ダイアログの表示をfalseにする
            state.outputExcelDialog = false;

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }
        }

        /**
         * 列設定アイコンクリック処理
         * 列設定アイコンの座標を取得し、列設定メニューコンポーネントのshowMenuを実行する
         */
        function showMenu() {
            const configIconCoordinate: ConfigIconCoordinate = { x: null, y: null };

            // 列設定アイコンの座標を取得する
            configIconCoordinate.x = document.querySelector('#column');
            configIconCoordinate.y = document.querySelector('#column');

            // 列設定メニューコンポーネントのshowMenuを実行する
            columnRef.value.showMenu(configIconCoordinate);
        }

        /**
         * 列設定メニューで、列設定情報が更新された際に実行する
         * 設定後のテーブルヘッダー情報を取得する
         * 
         * @param displayTableHeaders 画面に表示するテーブルヘッダー情報
         */
        function getTableHeader(displayTableHeaders: TableHeader[]) {
            // 設定後のテーブルヘッダー情報を取得する
            state.organizationDisplayTableHeaders = displayTableHeaders;
        }

        return {
            tableHeight,
            state: readonly(state),
            onColumns,
            commonRef,
            columnRef,
            child,
            page,
            itemsPerPage,
            linesPerPage,
            sortBy,
            sortDesc,
            isLoading,
            tableLoadingText,
            organizationInitialDisplayTableHeaders,
            organizationTableHeaders,
            updateFilter,
            updateOptions,
            onResize,
            navigationAdd,
            navigationUpdate,
            navigationDelete,
            showMenu,
            getTableHeader,
            outputExcelFile
        };
    }
});
